import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  FiClock,
  FiArrowLeft,
  FiArrowRight,
  FiFlag,
  FiCheck,
  FiAlertTriangle,
  FiPause,
  FiPlay,
  FiSave,
  FiSend,
  FiEye,
  FiEyeOff,
  FiMaximize,
  FiMinimize,
  FiBookOpen,
  FiBarChart2
} from 'react-icons/fi';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Badge from '../../components/common/Badge';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ExamModeSelector from '../../components/tests/ExamModeSelector';
import { useNotification } from '../../contexts/NotificationContext';
import { testAPI } from '../../services/api';
import './TakeTest.css';

const TakeTest = () => {
  const [test, setTest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isTestStarted, setIsTestStarted] = useState(false);
  const [isTestPaused, setIsTestPaused] = useState(false);
  const [isTestCompleted, setIsTestCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved');
  const [showModeSelector, setShowModeSelector] = useState(false);
  const [examMode, setExamMode] = useState(null);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [perPaperRemaining, setPerPaperRemaining] = useState({});

  const { id: testId } = useParams();
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();

  const navigationMode = location.state?.mode;
  const isExplanationMode = examMode === 'explanation';

  const getPaperBoundaries = useCallback(() => {
    const papers = (Array.isArray(test?.config?.papers) && test.config.papers.length > 0)
      ? test.config.papers
      : (Array.isArray(test?.papers) && test.papers.length > 0 ? test.papers : []);
    const total = questions.length;
    if (!Array.isArray(papers) || papers.length === 0 || total === 0) {
      return [{ number: 1, name: 'Paper 1', start: 0, end: total - 1, count: total, duration: test?.config?.duration || 0 }];
    }
    let start = 0;
    return papers.map((p, idx) => {
      const qCount = typeof p.totalQuestions === 'number' && p.totalQuestions > 0
        ? p.totalQuestions
        : (typeof p.questions === 'number' && p.questions > 0 ? p.questions : Math.round(total / papers.length));
      const safeCount = Math.min(qCount, Math.max(0, total - start));
      const boundary = { number: idx + 1, name: p.name || `Paper ${idx + 1}`, start, end: start + safeCount - 1, count: safeCount, duration: p.duration, sections: p.sections || [] };
      start += safeCount;
      return boundary;
    });
  }, [test, questions]);

  const getCurrentPaperInfo = useCallback(() => {
    const boundaries = getPaperBoundaries();
    const idx = currentQuestionIndex;
    const paper = boundaries.find(b => idx >= b.start && idx <= b.end) || boundaries[boundaries.length - 1];
    const localIndex = Math.max(0, idx - paper.start);
    let sectionName = null;
    if (Array.isArray(paper.sections) && paper.sections.length > 0) {
      const section = paper.sections.find(s => typeof s.startIndex === 'number' && typeof s.endIndex === 'number' && localIndex >= s.startIndex && localIndex <= s.endIndex);
      sectionName = section ? section.name : null;
    }
    return { paper, localIndex, sectionName, totalPapers: boundaries.length };
  }, [getPaperBoundaries, currentQuestionIndex]);

  const timerRef = useRef(null);
  const autoSaveRef = useRef(null);
  const warningTimeouts = useRef([]);
  const testContainerRef = useRef(null);

  useEffect(() => {
    const boundaries = getPaperBoundaries();
    if (boundaries && boundaries.length > 0) {
      const initial = {};
      boundaries.forEach(b => {
        const dur = typeof b.duration === 'number' && b.duration > 0 ? b.duration : (Math.floor((test?.config?.duration || 0) / boundaries.length));
        const seconds = dur > 0 && dur < 10000 ? Math.round(dur * 60) : Math.max(0, dur);
        initial[b.number] = seconds;
      });
      setPerPaperRemaining(initial);
    }
  }, [test, questions, getPaperBoundaries]);

  // Warn before closing/refreshing while insight is being generated
  useEffect(() => {
    if (!submitting) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [submitting]);

  // Memoized helpers to keep hook dependencies stable
  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stopAutoSave = useCallback(() => {
    if (autoSaveRef.current) {
      clearInterval(autoSaveRef.current);
      autoSaveRef.current = null;
    }
  }, []);

  const submitTest = useCallback(async (isAutoSubmit = false) => {
    try {
      setShowSubmitDialog(false);
      setSubmitting(true);
      const initialDuration = test?.config?.duration ?? test?.duration ?? 0;
      const timeSpentSeconds = Math.max(0, Number(initialDuration) - Number(timeRemaining));
      const submission = {
        answers,
        flaggedQuestions: Array.from(flaggedQuestions),
        timeSpent: timeSpentSeconds,
        timeRemaining,
        isAutoSubmit
      };
      const res = await testAPI.submitTest(testId, submission);
      const data = res?.data?.data ?? res?.data;
      const submissionId = data?.submissionId;
      setIsTestCompleted(true);
      stopTimer();
      stopAutoSave();
      showSuccess(isAutoSubmit ? 'Test auto-submitted due to time limit' : 'Test submitted successfully');
      navigate(`/tests/${testId}/results`, { state: submissionId ? { submissionId } : undefined });
    } catch (err) {
      showError('Failed to submit test');
    } finally {
      setSubmitting(false);
    }
  }, [answers, flaggedQuestions, timeRemaining, testId, test, stopTimer, stopAutoSave, navigate, showSuccess, showError]);

  const showTimeWarning = useCallback((minutes) => {
    setWarningMessage(`${minutes} minute${minutes !== 1 ? 's' : ''} remaining!`);
    setShowWarning(true);
    const timeout = setTimeout(() => {
      setShowWarning(false);
    }, 5000);
    warningTimeouts.current.push(timeout);
  }, []);

  const handleTimeUp = useCallback(async () => {
    await submitTest(true);
  }, [submitTest]);

  // Save progress (memoized to stabilize dependencies)
  const saveProgress = useCallback(async (showNotification = false) => {
    try {
      setSaving(true);
      setAutoSaveStatus('saving');
      await testAPI.saveTestProgress(testId, {
        answers,
        flaggedQuestions: Array.from(flaggedQuestions),
        currentQuestionIndex,
        timeRemaining,
        isStarted: isTestStarted,
        isPaused: isTestPaused
      });
      setAutoSaveStatus('saved');
      if (showNotification) {
        showSuccess('Progress saved successfully');
      }
    } catch (err) {
      setAutoSaveStatus('error');
      if (showNotification) {
        showError('Failed to save progress');
      }
    } finally {
      setSaving(false);
    }
  }, [testId, answers, flaggedQuestions, currentQuestionIndex, timeRemaining, isTestStarted, isTestPaused, showSuccess, showError]);

  // Auto-save timer (memoized)
  const startAutoSave = useCallback(() => {
    if (autoSaveRef.current) clearInterval(autoSaveRef.current);
    autoSaveRef.current = setInterval(() => {
      saveProgress();
    }, 30000);
  }, [saveProgress]);

  useEffect(() => {
    // Check if exam mode was passed from navigation or show selector
    if (navigationMode) {
      setExamMode(navigationMode);
    } else if (test && !examMode) {
      setShowModeSelector(true);
    }
  }, [test, navigationMode, examMode]);

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    
    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          handleTimeUp();
          return 0;
        }
        
        const minutesLeft = Math.floor(prev / 60);
        if (test?.settings?.timeWarnings?.includes(minutesLeft)) {
          showTimeWarning(minutesLeft);
        }
        
        return prev - 1;
      });
      const { paper } = getCurrentPaperInfo();
      setPerPaperRemaining(prevMap => {
        const next = { ...prevMap };
        if (paper && next[paper.number] > 0) {
          next[paper.number] = next[paper.number] - 1;
        }
        return next;
      });
    }, 1000);
  }, [test, handleTimeUp, showTimeWarning, getCurrentPaperInfo]);

  // stopTimer memoized above

  useEffect(() => {
    if (isTestStarted && !isTestPaused && !isTestCompleted && timeRemaining > 0) {
      startTimer();
    } else {
      stopTimer();
    }
    
    return () => stopTimer();
  }, [isTestStarted, isTestPaused, isTestCompleted, timeRemaining, startTimer, stopTimer]);

  useEffect(() => {
    if (isTestStarted && !isTestCompleted) {
      startAutoSave();
    } else {
      stopAutoSave();
    }
    
    return () => stopAutoSave();
  }, [isTestStarted, isTestCompleted, startAutoSave, stopAutoSave]);

  useEffect(() => {
    // Handle fullscreen change
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    // Prevent page refresh during test
    const handleBeforeUnload = (e) => {
      if (isTestStarted && !isTestCompleted) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isTestStarted, isTestCompleted]);

  const loadTest = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // First get test details
      const testResponse = await testAPI.getTest(testId);
      const { test: testData } = testResponse.data.data;
      
      setTest(testData);
      
      // Load questions dynamically from the new endpoint
      try {
        const questionsResponse = await testAPI.getTestQuestions(testId);
        const { questions: questionData, isDynamic, totalQuestions, syllabusInfo } = questionsResponse.data.data;
        
        // Ensure questions have consistent id field (map _id to id if needed)
        const normalizedQuestions = (questionData || []).map(question => ({
          ...question,
          id: question.id || question._id
        }));
        
        setQuestions(normalizedQuestions);
        
        // Update test config with actual question count if dynamic
        if (isDynamic && testData.config) {
          testData.config.totalQuestions = totalQuestions;
        }
        
        console.log(`Loaded ${totalQuestions} questions ${isDynamic ? 'dynamically' : 'from fixed set'}`);
        if (syllabusInfo) {
          console.log(`Syllabus-based questions for ${syllabusInfo.examType} ${syllabusInfo.subject}`);
        }
        
      } catch (questionsError) {
        console.error('Failed to load dynamic questions, falling back to test questions:', questionsError);
        // Fallback to questions from test data if dynamic loading fails
        const fallbackQuestions = (testData.questions || []).map(question => ({
          ...question,
          id: question.id || question._id
        }));
        setQuestions(fallbackQuestions);
      }
      
      setTimeRemaining(testData.config?.duration || testData.duration || 0);
      
      // Load existing progress if any
      if (testData.userProgress) {
        setAnswers(testData.userProgress.answers || {});
        setFlaggedQuestions(new Set(testData.userProgress.flaggedQuestions || []));
        setCurrentQuestionIndex(testData.userProgress.currentQuestionIndex || 0);
        
        if (testData.userProgress.isStarted) {
          setIsTestStarted(true);
          setTimeRemaining(testData.userProgress.timeRemaining || testData.config?.duration || testData.duration);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load test');
    } finally {
      setLoading(false);
    }
  }, [testId]);

  // Move loadTest effect below definition to satisfy no-use-before-define
  useEffect(() => {
    loadTest();
    const timeouts = warningTimeouts.current;
    return () => {
      stopTimer();
      stopAutoSave();
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, [testId, loadTest, stopTimer, stopAutoSave]);

  // stopAutoSave memoized above

  // showTimeWarning and handleTimeUp memoized above

  // saveProgress memoized above

  const handleStartTest = async () => {
    try {
      setIsTestStarted(true);
      await saveProgress();
      
      if (test?.settings?.requireFullscreen) {
        await enterFullscreen();
      }
    } catch (err) {
      showError('Failed to start test');
    }
  };

  const handlePauseTest = async () => {
    setIsTestPaused(true);
    await saveProgress(true);
  };

  const handleResumeTest = () => {
    setIsTestPaused(false);
  };

  const handleAnswerChange = (questionId, answerId) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answerId
    }));
    
    setAutoSaveStatus('unsaved');
  };

  const handleFlagQuestion = (questionId) => {
    setFlaggedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const handleNavigateQuestion = (index) => {
    setCurrentQuestionIndex(index);
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  // submitTest memoized above

  const handleSubmitTest = () => {
    setShowSubmitDialog(true);
  };

  const confirmSubmit = () => {
    setShowSubmitDialog(false);
    submitTest();
  };

  const enterFullscreen = async () => {
    try {
      if (testContainerRef.current && testContainerRef.current.requestFullscreen) {
        await testContainerRef.current.requestFullscreen();
      }
    } catch (err) {
      console.error('Failed to enter fullscreen:', err);
    }
  };

  const exitFullscreen = async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('Failed to exit fullscreen:', err);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimeColor = () => {
    if (timeRemaining <= 300) return 'danger'; // 5 minutes
    if (timeRemaining <= 900) return 'warning'; // 15 minutes
    return 'normal';
  };

  const getQuestionStatus = (index) => {
    const question = questions[index];
    if (!question) return 'unanswered';
    
    const isAnswered = answers[question.id];
    const isFlagged = flaggedQuestions.has(question.id);
    const isCurrent = index === currentQuestionIndex;
    
    if (isCurrent) return 'current';
    if (isAnswered && isFlagged) return 'answered-flagged';
    if (isAnswered) return 'answered';
    if (isFlagged) return 'flagged';
    return 'unanswered';
  };

  const getAnsweredCount = () => {
    return Object.keys(answers).length;
  };

  const getFlaggedCount = () => {
    return flaggedQuestions.size;
  };

  if (loading) {
    return (
      <div className="take-test-loading">
        <LoadingSpinner size="large" />
        <p>Loading test...</p>
      </div>
    );
  }

  if (error && !test) {
    return (
      <div className="take-test-error">
        <FiAlertTriangle size={48} />
        <h2>Unable to Load Test</h2>
        <p>{error}</p>
        <div className="error-actions">
          <Button onClick={() => navigate(-1)} variant="outline">
            Go Back
          </Button>
          <Button onClick={loadTest}>Try Again</Button>
        </div>
      </div>
    );
  }

  if (!isTestStarted) {
    return (
      <div className="test-start-screen">
        <div className="start-container">
          <div className="test-info">
            <h1>{test?.title}</h1>
            <p>{test?.description}</p>
            
            <div className="test-details">
              <div className="detail-item">
                <FiClock className="detail-icon" />
                <span>Duration: {formatTime(test?.duration || 0)}</span>
              </div>
              <div className="detail-item">
                <FiBookOpen className="detail-icon" />
                <span>Questions: {test?.totalQuestions}</span>
              </div>
              <div className="detail-item">
                <FiBarChart2 className="detail-icon" />
                <span>Total Points: {test?.totalPoints}</span>
              </div>
            </div>
            
            {test?.settings?.requireFullscreen && (
              <div className="fullscreen-notice">
                <FiMaximize className="notice-icon" />
                <p>This test requires fullscreen mode for security purposes.</p>
              </div>
            )}
            
            <div className="start-actions">
              <Button
                onClick={handleStartTest}
                size="large"
                icon={FiPlay}
              >
                Start Test
              </Button>
              <Button
                onClick={() => navigate(-1)}
                variant="outline"
                size="large"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isTestPaused) {
    return (
      <div className="test-paused-screen">
        <div className="pause-container">
          <FiPause size={64} className="pause-icon" />
          <h2>Test Paused</h2>
          <p>Your progress has been saved. Click resume to continue.</p>
          
          <div className="pause-info">
            <div className="info-item">
              <span>Time Remaining: {formatTime(timeRemaining)}</span>
            </div>
            <div className="info-item">
              <span>Questions Answered: {getAnsweredCount()}/{questions.length}</span>
            </div>
          </div>
          
          <div className="pause-actions">
            <Button
              onClick={handleResumeTest}
              size="large"
              icon={FiPlay}
            >
              Resume Test
            </Button>
            <Button
              onClick={() => saveProgress(true)}
              variant="outline"
              loading={saving}
              icon={FiSave}
            >
              Save Progress
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div className="take-test" ref={testContainerRef}>
      {submitting && (
        <div className="take-test-submit-overlay" aria-live="polite" aria-busy="true">
          <div className="take-test-submit-overlay-content">
            <LoadingSpinner size="large" />
            <p className="take-test-submit-overlay-title">Generating Expert Intelligence insight…</p>
            <p className="take-test-submit-overlay-sub">Please wait. Do not close or leave this page.</p>
          </div>
        </div>
      )}
      {showWarning && (
        <div className="time-warning">
          <FiAlertTriangle className="warning-icon" />
          <span>{warningMessage}</span>
        </div>
      )}

      <div className="test-header">
        <div className="header-left">
          <h1 className="test-title">{test?.title}</h1>
          <div className="test-info">
            <div className="paper-indicator">
              {(() => {
                const { paper, localIndex, sectionName, totalPapers } = getCurrentPaperInfo();
                return (
                  <>
                    <Badge variant="primary">Paper {paper.number} of {totalPapers}</Badge>
                    <Badge variant="outline">{paper.name}</Badge>
                    <Badge variant="secondary">{localIndex + 1} of {paper.count}</Badge>
                    {sectionName && <Badge variant="info">{sectionName}</Badge>}
                  </>
                );
              })()}
            </div>
            <div className="test-progress">
              <span>Question {currentQuestionIndex + 1} of {questions.length}</span>
            </div>
            <Badge className={`mode-badge ${examMode}`}>
              {isExplanationMode ? 'Explanation Mode' : 'Normal Mode'}
            </Badge>
          </div>
        </div>
        
        <div className="header-center">
          <div className="test-stats">
            <div className="stat-item">
              <FiCheck className="stat-icon" />
              <span>{getAnsweredCount()}/{questions.length} Answered</span>
            </div>
            <div className="stat-item">
              <FiFlag className="stat-icon" />
              <span>{getFlaggedCount()} Flagged</span>
            </div>
          </div>
        </div>
        
        <div className="header-right">
          <div className="timer">
            <FiClock className="timer-icon" />
            <span className={`timer-text ${getTimeColor()}`}>
              {formatTime(timeRemaining)}
            </span>
          </div>
          <div className="paper-timer">
            <FiClock className="timer-icon" />
            {(() => {
              const { paper } = getCurrentPaperInfo();
              const secs = perPaperRemaining[paper?.number] ?? 0;
              return (
                <span className={`timer-text ${secs <= 300 ? 'danger' : secs <= 900 ? 'warning' : 'normal'}`}>
                  {formatTime(secs)}
                </span>
              );
            })()}
          </div>
          
          <div className="header-actions">
            <Button
              variant="text"
              onClick={() => saveProgress(true)}
              loading={saving}
              icon={FiSave}
              size="small"
            >
              {autoSaveStatus === 'saving' ? 'Saving...' : 
               autoSaveStatus === 'saved' ? 'Saved' : 
               autoSaveStatus === 'error' ? 'Error' : 'Save'}
            </Button>
            
            <Button
              variant="text"
              onClick={handlePauseTest}
              icon={FiPause}
              size="small"
            >
              Pause
            </Button>
            
            {test?.settings?.requireFullscreen && (
              <Button
                variant="text"
                onClick={isFullscreen ? exitFullscreen : enterFullscreen}
                icon={isFullscreen ? FiMinimize : FiMaximize}
                size="small"
              >
                {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="test-content">
        {getPaperBoundaries().length > 1 && (
          <div className="paper-tabs">
            {getPaperBoundaries().map((b) => (
              <button
                key={b.number}
                className={`paper-tab ${currentQuestionIndex >= b.start && currentQuestionIndex <= b.end ? 'active' : ''}`}
                onClick={() => handleNavigateQuestion(b.start)}
                title={b.name}
              >
                {b.name}
              </button>
            ))}
          </div>
        )}
        <div className="question-nav">
          <div className="nav-header">
            <h3>Questions</h3>
            <Button
              variant="text"
              onClick={() => setShowReview(!showReview)}
              icon={showReview ? FiEyeOff : FiEye}
              size="small"
            >
              {showReview ? 'Hide' : 'Review'}
            </Button>
          </div>
          
          <div className="question-grid">
            {questions.map((_, index) => (
              <button
                key={index}
                className={`question-nav-item ${getQuestionStatus(index)}`}
                onClick={() => handleNavigateQuestion(index)}
              >
                {index + 1}
              </button>
            ))}
          </div>
          
          {showReview && (
            <div className="review-summary">
              <div className="summary-item">
                <div className="summary-color answered"></div>
                <span>Answered ({getAnsweredCount()})</span>
              </div>
              <div className="summary-item">
                <div className="summary-color flagged"></div>
                <span>Flagged ({getFlaggedCount()})</span>
              </div>
              <div className="summary-item">
                <div className="summary-color unanswered"></div>
                <span>Unanswered ({questions.length - getAnsweredCount()})</span>
              </div>
            </div>
          )}
        </div>

        <div className="question-content">
          {currentQuestion && (
            <div className="question-container">
              <div className="question-header">
                <div className="question-info">
                  <Badge variant="outline">Question {currentQuestionIndex + 1}</Badge>
                  <Badge variant="secondary">{currentQuestion.points} points</Badge>
                  <Badge variant={currentQuestion.difficulty === 'easy' ? 'success' : 
                                  currentQuestion.difficulty === 'intermediate' ? 'warning' : 'danger'}>
                    {currentQuestion.difficulty}
                  </Badge>
                  <Button
                    variant="text"
                    onClick={() => handleFlagQuestion(currentQuestion.id)}
                    icon={FiFlag}
                    className={flaggedQuestions.has(currentQuestion.id) ? 'flagged' : ''}
                  >
                    {flaggedQuestions.has(currentQuestion.id) ? 'Unflag' : 'Flag'}
                  </Button>
                </div>
                
                <div className="question-text">
                  <h2>{currentQuestion.question?.text || currentQuestion.question?.question || (typeof currentQuestion.question === 'string' ? currentQuestion.question : 'Question text not available')}</h2>
                </div>
                
                <div className="question-options">
                  {currentQuestion.options && currentQuestion.options.map((option) => {
                    const optionId = option._id || option.id;
                    const isSelected = answers[currentQuestion.id] === optionId;
                    const isLocked = isExplanationMode && answers[currentQuestion.id] !== undefined;
                    
                    return (
                      <label
                        key={optionId}
                        className={`option-label ${isSelected ? 'selected' : ''} ${isLocked ? 'locked' : ''}`}
                      >
                        <input
                          type="radio"
                          name={`question-${currentQuestion.id}`}
                          value={optionId}
                          checked={isSelected}
                          onChange={() => !isLocked && handleAnswerChange(currentQuestion.id, optionId)}
                          disabled={isLocked}
                        />
                        <div className="option-content">
                          <span className="option-text">{option.text}</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
                
                {isExplanationMode && answers[currentQuestion.id] !== undefined && (currentQuestion.explanation?.text || currentQuestion.explanation) && (
                  <div className="explanation-section">
                    <div className="explanation-header">
                      <FiBookOpen className="explanation-icon" />
                      <h3>Explanation</h3>
                    </div>
                    <div className="explanation-content">
                      <p>{currentQuestion.explanation?.text || (typeof currentQuestion.explanation === 'string' ? currentQuestion.explanation : 'No explanation available')}</p>
                      {currentQuestion.reference && (
                        <div className="explanation-reference">
                          <strong>Reference:</strong> {currentQuestion.reference}
                        </div>
                      )}
                    </div>
                    <div className="explanation-actions">
                      {currentQuestionIndex < questions.length - 1 && (
                        <Button
                          onClick={handleNextQuestion}
                          icon={FiArrowRight}
                          iconPosition="right"
                          size="small"
                        >
                          Next Question
                        </Button>
                      )}
                      {currentQuestionIndex === questions.length - 1 && (
                        <Button
                          onClick={handleSubmitTest}
                          loading={submitting}
                          icon={FiSend}
                          variant="success"
                          size="small"
                        >
                          Submit Test
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="test-footer">
        {!isExplanationMode && (
          <>
            <div className="footer-left">
              <Button
                onClick={handlePreviousQuestion}
                disabled={currentQuestionIndex === 0}
                variant="outline"
                icon={FiArrowLeft}
              >
                Previous
              </Button>
            </div>
            
            <div className="footer-center">
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                />
              </div>
              <span className="progress-text">
                {currentQuestionIndex + 1} of {questions.length}
              </span>
            </div>
            
            <div className="footer-right">
              {currentQuestionIndex < questions.length - 1 ? (
                <Button
                  onClick={handleNextQuestion}
                  icon={FiArrowRight}
                  iconPosition="right"
                >
                  Next
                </Button>
              ) : (
                <Button
                  onClick={handleSubmitTest}
                  loading={submitting}
                  icon={FiSend}
                  variant="success"
                >
                  Submit Test
                </Button>
              )}
            </div>
          </>
        )}
        
        {isExplanationMode && (
          <div className="footer-center">
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
              />
            </div>
            <span className="progress-text">
              {currentQuestionIndex + 1} of {questions.length}
            </span>
          </div>
        )}
      </div>

      {showModeSelector && (
        <ExamModeSelector
          test={test}
          onModeSelect={(mode) => {
            setExamMode(mode);
            setShowModeSelector(false);
          }}
          onClose={() => setShowModeSelector(false)}
        />
      )}

      {showSubmitDialog && (
        <ConfirmDialog
          isOpen={showSubmitDialog}
          onClose={() => setShowSubmitDialog(false)}
          onConfirm={confirmSubmit}
          title="Submit Test"
          message={
            questions.length - Object.keys(answers).length > 0
              ? `You have ${questions.length - Object.keys(answers).length} unanswered question${questions.length - Object.keys(answers).length !== 1 ? 's' : ''}. Are you sure you want to submit your test?`
              : 'Are you sure you want to submit your test? You will not be able to make changes after submission.'
          }
          confirmText="Submit Test"
          cancelText="Continue Test"
          variant="warning"
          loading={submitting}
        />
      )}
    </div>
  );
};

export default TakeTest;
